import { ChatMessage } from './messageHelpers';

/**
 * Merge adjacent assistant messages: if next message(s) are tool-only,
 * prepend their tools to current message so tools render before content.
 *
 * Example:
 * Message 1: [text, widget]
 * Message 2: [tool-getMetrics]          (tool-only)
 * Message 3: [tool-getSeasons]          (tool-only)
 * Message 4: [text: "Here's the data"]
 *
 * Result:
 * Message 1: [tool-getMetrics, tool-getSeasons, text, widget]
 * Message 2-3: (skipped)
 * Message 4: (unchanged)
 */
export function mergeToolMessages(messages: ChatMessage[]): ChatMessage[] {
  const processedMessages: ChatMessage[] = [];
  const skippedIndices = new Set<number>();

  for (let i = 0; i < messages.length; i++) {
    if (skippedIndices.has(i)) continue;

    const currentMsg = messages[i];

    // Check ALL consecutive tool-only messages after current message
    if (currentMsg.role === 'assistant') {
      const toolOnlyParts: any[] = [];
      let lookAheadIndex = i + 1;

      // Keep looking ahead while we find tool-only assistant messages
      while (lookAheadIndex < messages.length) {
        const candidateMsg = messages[lookAheadIndex];

        if (candidateMsg.role !== 'assistant') break;

        const hasOnlyTools = candidateMsg.parts?.every(
          (p) =>
            p.type?.startsWith('tool-') &&
            (p.state === 'approval-requested' ||
              p.state === 'approval-responded' ||
              p.state === 'output-available' ||
              p.state === 'output-error')
        );

        const hasContent = candidateMsg.parts?.some(
          (p) => p.type === 'text' || p.type === 'data-widget' || p.type === 'data-custom-reasoning'
        );

        // If this message has content, stop looking ahead
        if (!hasOnlyTools || hasContent) break;

        // Found a tool-only message - collect its parts and mark for skipping
        if (candidateMsg.parts) {
          toolOnlyParts.push(...candidateMsg.parts);
          skippedIndices.add(lookAheadIndex);
        }

        lookAheadIndex++;
      }

      // If we found any tool-only messages, merge them into current message (tools FIRST)
      if (toolOnlyParts.length > 0) {
        processedMessages.push({
          ...currentMsg,
          parts: [...toolOnlyParts, ...(currentMsg.parts || [])],
        });
        continue;
      }
    }

    processedMessages.push(currentMsg);
  }

  return processedMessages;
}
